import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { adobeStandardSearchEvent } from '../../../common/AdobeTagging/adobeStandardSearchEvent';
import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { LocationLabel } from '../../../common/PSXHeader/LocationInput/LocationInput.styled';
import { useAdobePageTrackEvent } from '../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useGetFipsCode } from '../../../hooks/useGetFipsCode';
import { useGetPlanDefinitions } from '../../../hooks/useGetPlanDefinitions/useGetPlanDefinitions';
import { useLagoon } from '../../../hooks/useLagoon';
import { DateTime } from '../../../utils/DateTime';
import {
  buildPortalData,
  filterUniquePlanNames,
  getGuestLocationPageName,
  getLobForPortalData,
  getLocationRequirement,
  shouldSkipPlanSelection,
} from '../Guest.utils';
import { getBlueGreenGQLApiUrl } from '../utils';
import {
  ButtonContainer,
  LocationInputContainer,
  LocationModal,
  LocationModalSection,
  MessagingText,
} from './GuestLocation.style';
import {
  GuestLocation,
  getShowDualCompletePlanMessaging,
  isSinglePlan,
} from './GuestLocation.utils';
import { GuestLocationInput } from './GuestLocationInput';
import {
  LocationSearchBoxWrapper,
  SelectedLocationWrapper,
} from './GuestLocationSelect.style';

type planDetails = {
  medicarePlanType: string;
  planIdentifier: string;
  planName: string;
  searchDirectory: string;
  years: [PlanYear];
};

type PlanYear = {
  planYear: string;
  reciprocityId: string;
  telehealthIndicator: boolean;
  displayAcceptsMedicaid: boolean;
  clusterKey: string;
  groupDriven: boolean;
  guestPath: string;
};
export const GuestLocationSelect = ({
  selectedLocation,
  setSelectedLocation,
  isDataLoaded,
  setIsDataLoaded,
}: GuestLocationInputProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useState<GuestLocation>(
    selectedLocation || ({} as GuestLocation)
  );
  const [currentInputValue, setCurrentInputValue] = useState('');
  useEffect(() => {
    if (selectedLocation) {
      setLocation(selectedLocation);
    }
  }, [selectedLocation]);
  useEffect(() => {
    if (location?.label) {
      setCurrentInputValue(location.label);
    }
  }, [location?.label]);
  const storedPlans = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS
  );
  const [availablePlans, setAvailablePlans] = useState<any[]>(
    storedPlans ? JSON.parse(storedPlans) : []
  );
  const stateCode = location?.stateCode;
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const lagoonUIMessage = useLagoon('ui-messaging')();
  const selectLocationMessageText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_MESSAGE,
  })?.message;
  const locationModalHeadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_HEADING,
  })?.message;
  const locationModalSubheadingText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SUBHEADING,
  })?.message;
  const locationModalSignInText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SIGN_IN,
  })?.message;
  const locationModalSignInUrl = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SIGN_IN,
  })?.url;
  const locationModalSearchText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.LOCATION_MODAL_SEARCH,
  })?.message;
  const dualCompletePlanMessaging = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.DUAL_COMPLETE_PLAN_MESSAGING,
  })?.message;

  const coverageType = searchParams?.get('coverageType') || '';
  const lob = searchParams?.get('planSelectionLob') || '';

  const selectLocationHeadingText = find(lagoonUIMessage, {
    key: isSinglePlan(lob, coverageType)
      ? ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_SINGLE_PLAN_HEADING
      : ConstantsLagoon.GUEST_PLAN_SELECTION.SELECT_LOCATION_HEADING,
  })?.message;

  const isButtonDisabled =
    selectedLocation === undefined ||
    !isDataLoaded ||
    !currentInputValue?.trim();

  const showDualCompletePlanMessaging = getShowDualCompletePlanMessaging(
    coverageType,
    lob,
    selectedLocation
  );

  const navigateToLandingPage = (location) => {
    const chipValue = searchParams?.get('chipValue') || '';
    const plansAvailable = JSON.parse(
      sessionStorage.getItem(Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS) ||
        '[]'
    );
    const selectedPlan = plansAvailable[0]?.planDetails?.[0];
    const stateCode = location?.stateCode;

    if (shouldSkipPlanSelection(plansAvailable?.length)) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(location)
      );
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA,
        JSON.stringify({
          selectedPlan,
          lob,
          coverageType: coverageType || 'M',
          stateCode: stateCode || '',
        })
      );
      navigate(`/browse?coverageType=${coverageType}&chipValue=${chipValue}`, {
        state: buildPortalData(selectedPlan, lob, coverageType, stateCode),
      });
    }
  };

  const currentYear =
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear();

  const flags = useFlags();
  const guestBGApiUrl = getBlueGreenGQLApiUrl(flags, searchParams);

  const [, getPlanDetails] = useGetPlanDefinitions({
    url: guestBGApiUrl,
    onCompleted: (result: {
      data: {
        getPlanDefinitions: {
          planCategories: [
            { planCategory: string; planDetails: [planDetails] }
          ];
          planDetails: [planDetails];
        };
      };
    }) => {
      const planDefinitions = result?.data?.getPlanDefinitions;

      const uniqueCategoryPlanNames = new Set<string>();
      const planCategoryList = planDefinitions?.planCategories
        ? planDefinitions?.planCategories
            ?.map((category) => {
              const uniquePlansInCategory = new Set<string>();
              const filteredPlanDetails = filterUniquePlanNames(
                category?.planDetails,
                uniquePlansInCategory
              );

              return {
                planCategory: category?.planCategory,
                planDetails: filteredPlanDetails,
              };
            })
            .filter((category) => {
              if (category.planDetails && category.planDetails.length > 0) {
                const planName = category.planDetails[0]?.planName;
                if (planName && !uniqueCategoryPlanNames.has(planName)) {
                  uniqueCategoryPlanNames.add(planName);
                  return true;
                }
              }
              return false;
            })
        : [];

      const uniqueDetailPlanNames = new Set<string>();
      const filteredPlanDetails = filterUniquePlanNames(
        planDefinitions?.planDetails,
        uniqueDetailPlanNames
      );

      const planDetailsList = filteredPlanDetails.map((plan) => ({
        planDetails: [plan],
      }));

      const combinedPlans = [
        ...(planCategoryList || []),
        ...(planDetailsList || []),
      ];
      setAvailablePlans(combinedPlans || []);
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.AVAILABLE_PLANS,
        JSON.stringify(combinedPlans || [])
      );
      setIsDataLoaded(true);
    },
  });

  const locationRequirement = getLocationRequirement(lob, coverageType);
  const [, getFipsCode] = useGetFipsCode({});

  const isEI = lob === 'EI';

  useEffect(() => {
    setIsDataLoaded(false);
    sessionStorage.setItem('countyFipsCode', JSON.stringify(null));
    const planIdentifier = sessionStorage.getItem(
      Constants.STORAGE_KEYS.SESSION.DELSYS_CODE
    );
    if (locationRequirement === 'n/a') {
      getPlanDetails({
        variables: {
          planYear: String(currentYear),
          lob,
          coverageType,
          isDualYear: true,
          isEI,
          planIdentifier,
        },
      });
    } else if (
      locationRequirement === 'county' &&
      location?.latitude &&
      location?.longitude
    ) {
      getFipsCode({
        variables: {
          lat: parseFloat(location.latitude).toFixed(10),
          long: parseFloat(location.longitude).toFixed(10),
        },
      }).then((res) => {
        const countyFipsCode = res?.data?.getFipsCode?.fipsCode;
        sessionStorage.setItem('countyFipsCode', countyFipsCode);
        getPlanDetails({
          variables: {
            planYear: String(currentYear),
            lob,
            coverageType,
            stateCode,
            countyFipsCode,
            isDualYear: true,
            isEI,
            planIdentifier,
          },
        });
      });
    } else {
      if (location?.stateCode) {
        getPlanDetails({
          variables: {
            planYear: String(currentYear),
            lob,
            coverageType,
            stateCode,
            isDualYear: true,
            isEI,
            planIdentifier,
          },
        });
      }
    }
  }, [
    location?.latitude,
    location?.longitude,
    location?.stateCode,
    lob,
    coverageType,
    currentYear,
  ]);

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    guestLob: getLobForPortalData(lob),
    isGuest: true,
    pageName: getGuestLocationPageName(coverageType),
    sitesectionLevel1: 'guest plan selection',
    sitesectionLevel2: 'select coverage type',
  });

  useEffect(() => {
    adobePageTrackEvent();
  }, []);

  const locationModalAdobeLabel = 'edit location modal';
  useEffect(() => {
    if (isOpen) {
      adobeModalTrackEvent(locationModalAdobeLabel);
    }
  }, [isOpen]);

  return (
    <React.Fragment>
      <LocationModal
        isOpen={isOpen}
        onClose={() => {
          adobeLinkTrackEvent({
            name: 'close',
            location: locationModalAdobeLabel,
            type: 'internal',
            destinationUrl: '',
          });
          setIsOpen(false);
        }}
        title={<Heading offset={3}>{locationModalHeadingText}</Heading>}
      >
        <LocationModalSection>
          <Text
            css={{ marginBottom: '8px' }}
            data-auto-testid="modal-location-heading"
            data-testid="modal-location-heading"
          >
            {locationModalSubheadingText}
          </Text>
          <GuestLocationSearch
            currentInputValue={currentInputValue}
            isDataLoaded={isDataLoaded}
            isLocationModal={true}
            location={location}
            setCurrentInputValue={setCurrentInputValue}
            setIsDataLoaded={setIsDataLoaded}
            setLocation={setLocation}
            setSelectedLocation={setSelectedLocation}
          />
          <ButtonContainer>
            <Button
              data-auto-testid="modal-search-button"
              data-testid="modal-search-button"
              disabled={isButtonDisabled}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: locationModalSearchText,
                  location: locationModalAdobeLabel,
                  type: 'internal',
                  destinationUrl: '',
                });
                if (location) {
                  adobeStandardSearchEvent({
                    isGuest: true,
                    numberOfResults: availablePlans.length,
                    method: 'autofill',
                    term: location?.label || '',
                    type: 'site',
                  });
                  setSelectedLocation(location);
                }
                setIsOpen(false);
                navigateToLandingPage(location);
              }}
              size="$md"
              type="button"
              variant="solid"
            >
              {locationModalSearchText}
            </Button>
            <Button
              css={{ marginLeft: '8px' }}
              data-auto-testid="modal-sign-in-button"
              data-testid="modal-sign-in-button"
              href={locationModalSignInUrl}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: locationModalSignInText,
                  location: locationModalAdobeLabel,
                  type: 'internal',
                  destinationUrl: locationModalSignInUrl,
                });
              }}
              openNewWindow={false}
              size="$md"
              variant="outline"
            >
              {locationModalSignInText}
            </Button>
          </ButtonContainer>
        </LocationModalSection>
      </LocationModal>
      <Flex
        alignContent="center"
        css={{ marginBottom: '20px', gap: '8px' }}
        direction="column"
        justify="center"
      >
        <Heading offset={3} textAlign="center">
          {selectLocationHeadingText}
        </Heading>
        <MessagingText size="$md">{selectLocationMessageText}</MessagingText>
        {showDualCompletePlanMessaging && (
          <MessagingText
            data-auto-testid="dual-complete-plan-messaging"
            data-testid="dual-complete-plan-messaging"
            size="$md"
          >
            {dualCompletePlanMessaging}
          </MessagingText>
        )}
      </Flex>
      {selectedLocation ? (
        <SelectedLocationWrapper
          alignItems="center"
          direction="row"
          id="selected-location-container"
          justify="center"
        >
          <IconSymbol color="$gray8" icon="location_on" />
          <Text>{selectedLocation?.label}</Text>
          <Button
            aria-haspopup="dialog"
            data-testid="edit-location-button"
            onClick={() => {
              adobeLinkTrackEvent({
                name: t('PRIMARY_CARE_PROVIDER.EDIT'),
                location: 'Choose a plan',
                type: 'internal',
                destinationUrl: '',
              });
              setIsOpen(true);
            }}
            size={'$sm'}
            variant="tertiary"
          >
            {t('PRIMARY_CARE_PROVIDER.EDIT')}
          </Button>
        </SelectedLocationWrapper>
      ) : (
        <LocationSearchBoxWrapper justify="center">
          <GuestLocationSearch
            availablePlans={availablePlans}
            currentInputValue={currentInputValue}
            isDataLoaded={isDataLoaded}
            location={location}
            setCurrentInputValue={setCurrentInputValue}
            setIsDataLoaded={setIsDataLoaded}
            setLocation={setLocation}
            setSelectedLocation={setSelectedLocation}
          />
        </LocationSearchBoxWrapper>
      )}
    </React.Fragment>
  );
};

type GuestLocationInputProps = {
  selectedLocation?: GuestLocation;
  setSelectedLocation: (a: GuestLocation) => void;
  isLocationModal?: boolean;
  location?: GuestLocation;
  setLocation?: (a: GuestLocation) => void;
  isDataLoaded: boolean;
  setIsDataLoaded: (a: boolean) => void;
  availablePlans?: any[];
  currentInputValue?: string;
  setCurrentInputValue?: (value: string) => void;
};

const GuestLocationSearch = ({
  setSelectedLocation,
  isLocationModal,
  location,
  setLocation,
  isDataLoaded,
  availablePlans = [],
  setCurrentInputValue = () => {},
}: GuestLocationInputProps) => {
  const { t } = useTranslation();
  const displaySelectButton = !isLocationModal;
  const [isOnFocus, setIsOnFocus] = useState(false);
  const isLocationModalInFocus = isOnFocus && isLocationModal;
  const isButtonDisabled = location === undefined || !isDataLoaded;
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const coverageType = searchParams?.get('coverageType') || '';
  const lob = searchParams?.get('planSelectionLob') || '';
  const chipValue = searchParams?.get('chipValue') || '';

  const planSelectionConfig = useLagoon('config')();
  const signInUrl = planSelectionConfig?.find(
    (row) => row.key === `SIGNIN_URL_${lob?.toUpperCase()}`
  )?.value;

  const stateCode = location?.stateCode;

  const selectedPlan = availablePlans[0]?.planDetails?.[0];

  const submitButton = () => {
    if (location) {
      setSelectedLocation(location);
    }

    adobeStandardSearchEvent({
      isGuest: true,
      numberOfResults: availablePlans.length,
      method: 'autofill',
      term: location?.label || '',
      type: 'site',
    });

    if (shouldSkipPlanSelection(availablePlans.length)) {
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA,
        JSON.stringify({
          selectedPlan: availablePlans[0]?.planDetails?.[0],
          lob,
          coverageType: coverageType || 'M',
          stateCode: stateCode || '',
        })
      );
      sessionStorage.setItem(
        Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION,
        JSON.stringify(location)
      );
      const searchTerm = sessionStorage.getItem(
        Constants.STORAGE_KEYS.SESSION.SEARCH
      );

      const currentYear =
        DateTime.getCurrentDateTimeWithCentralOffset().getFullYear();
      const guestPath = selectedPlan?.years?.find(
        (year) => year?.planYear === String(currentYear)
      )?.guestPath;

      if (guestPath) {
        window.open(guestPath, '_blank');
        return;
      } else if (searchTerm) {
        navigate(
          `/browse/results/?coverageType=${coverageType}&search=${encodeURIComponent(
            searchTerm
          )}&psx-source=deeplink`,
          {
            state: buildPortalData(
              selectedPlan,
              lob,
              coverageType || 'M',
              stateCode || '',
              undefined,
              signInUrl
            ),
          }
        );
      } else {
        navigate(
          `/browse?coverageType=${coverageType}&chipValue=${chipValue}`,
          {
            state: buildPortalData(
              selectedPlan,
              lob,
              coverageType || 'M',
              stateCode || '',
              undefined,
              signInUrl
            ),
          }
        );
      }
    } else {
      navigate(
        `/plan-selection?planSelectionLob=${lob}&coverageType=${coverageType}&chipValue=${chipValue}`,
        {
          state: availablePlans,
          replace: true,
        }
      );
    }
  };
  return (
    <LocationInputContainer
      id="location-input-container"
      isLocationModal={isLocationModal}
      isLocationModalInFocus={isLocationModalInFocus}
    >
      <div id="location-search-input-area">
        <Text css={LocationLabel}>{'Location'}</Text>
        <GuestLocationInput
          isLocationModal={isLocationModal}
          location={location}
          setCurrentInputValue={setCurrentInputValue}
          setIsOnFocus={setIsOnFocus}
          setLocation={setLocation}
        />
      </div>
      {displaySelectButton && (
        <div style={{ alignSelf: 'center' }}>
          <Button
            data-auto-testid="submit-button"
            data-testid="submit-button"
            disabled={!location?.label || isButtonDisabled}
            onClick={submitButton}
            size="$md"
            type="submit"
          >
            {t('COMPARE_BUTTONS.SELECT')}
          </Button>
        </div>
      )}
    </LocationInputContainer>
  );
};
